import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Info, Plus, Trash2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { TIER_ICON_NAMES, tierIcon, tierTint } from '@/lib/tiers'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import TierBadge from '@/components/ui/TierBadge'
import { Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface TierRow {
  id: number
  name: string
  topup_threshold: string | number
  color?: string
  icon?: string
}

interface PricingData {
  tiers: TierRow[]
}

interface TierForm {
  name: string
  topup_threshold: string
  color: string
  icon: string
}

const EMPTY_FORM: TierForm = { name: '', topup_threshold: '', color: '#8B5CF6', icon: 'gem' }

function formFrom(tier: TierRow): TierForm {
  return {
    name: tier.name,
    topup_threshold: String(Number(tier.topup_threshold)),
    color: tier.color ?? '#94A3B8',
    icon: tier.icon ?? 'medal',
  }
}

function IconPicker({ value, color, onChange }: { value: string; color: string; onChange: (icon: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {TIER_ICON_NAMES.map((name) => {
        const Icon = tierIcon(name)
        const selected = value === name
        return (
          <button
            key={name}
            type="button"
            title={name}
            onClick={() => onChange(name)}
            className={cn(
              'flex size-8 items-center justify-center rounded-lg border transition',
              selected ? 'ring-2 ring-offset-1' : 'border-line text-muted hover:text-foreground',
            )}
            style={selected ? { ...tierTint(color), ['--tw-ring-color' as string]: `${color}66` } : undefined}
          >
            <Icon className="size-4" />
          </button>
        )
      })}
    </div>
  )
}

function ColorField({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#94A3B8'}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="size-10 shrink-0 cursor-pointer rounded-md border border-line bg-background p-1"
      />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="#FBBF24" className="font-mono" />
    </div>
  )
}

export default function AdminPricing() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [edits, setEdits] = useState<Record<number, TierForm>>({})
  const [draft, setDraft] = useState<TierForm>(EMPTY_FORM)
  const [confirmDelete, setConfirmDelete] = useState<TierRow | null>(null)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-pricing'],
    queryFn: async () => (await api.get<PricingData>('/admin/pricing')).data,
  })

  const invalidateTierData = () => {
    for (const key of [
      'admin-pricing',
      'storefront-tiers',
      'storefront-products',
      'storefront-product',
      'wallet',
      'products',
      'admin-products',
      'admin-customers',
    ]) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const saveTier = useMutation({
    mutationFn: async (tier: TierRow) => {
      const form = edits[tier.id] ?? formFrom(tier)
      return (
        await api.patch(`/admin/tiers/${tier.id}`, { ...form, topup_threshold: Number(form.topup_threshold) })
      ).data
    },
    onSuccess: (res, tier) => {
      invalidateTierData()
      setEdits(({ [tier.id]: _saved, ...rest }) => rest)
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const addTier = useMutation({
    mutationFn: async () =>
      (await api.post('/admin/tiers', { ...draft, topup_threshold: Number(draft.topup_threshold) })).data,
    onSuccess: (res) => {
      invalidateTierData()
      setDraft(EMPTY_FORM)
      toast.success(res.message)
    },
    onError: (err) => toast.error('Add failed', errorMessage(err)),
  })

  const deleteTier = useMutation({
    mutationFn: async (tierId: number) => (await api.delete(`/admin/tiers/${tierId}`)).data,
    onSuccess: (res) => {
      invalidateTierData()
      setConfirmDelete(null)
      toast.success(res.message)
    },
    onError: (err) => {
      setConfirmDelete(null)
      toast.error('Delete failed', errorMessage(err))
    },
  })

  if (isLoading) return <LogoLoader />

  // A failed load used to fall through to the skeleton forever, which reads as
  // a hung page rather than a broken request. Say what happened and offer a
  // retry instead.
  if (isError || !data) {
    return (
      <div>
        <PageHeader
          title="Pricing & tiers"
          subtitle="Tiers unlock flat RM-per-piece prices - set each product's tier prices in Product setup"
        />
        <Card className="text-center">
          <p className="font-semibold">Could not load the tiers</p>
          <p className="mt-1 text-sm text-foreground/60">{errorMessage(error)}</p>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // The API always sends a tiers array, but a proxy or error page answering 200
  // with some other body would otherwise crash the whole screen here.
  const tiers = data.tiers ?? []

  return (
    <div>
      <PageHeader
        title="Pricing & tiers"
        subtitle="Tiers unlock flat RM-per-piece prices - set each product's tier prices in Product setup"
      />

      <Card className="mb-5 border-primary/20 bg-primary/5">
        <div className="flex gap-3">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="space-y-1.5 text-sm text-foreground/80">
            <p className="text-2xs font-bold uppercase tracking-wide text-primary">How tiers work</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                A customer's tier is unlocked by their <strong>accumulated lifetime top-ups</strong> - every top-up
                counts toward the threshold, and spending from the wallet never downgrades a tier.
              </li>
              <li>
                Each tier holds a <strong>flat RM price per piece</strong> for every product - there are no percentage
                discounts. Set them per product in{' '}
                <Link to="/admin/products" className="font-semibold text-primary hover:underline">
                  Product setup
                </Link>
                .
              </li>
              <li>
                The storefront automatically shows the % savings between tiers, computed from these prices, and invites
                customers to top up to the next tier.
              </li>
              <li>Edits apply to all customers' future orders as soon as you save.</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <SectionLabel>Tiers (lifetime top-up unlocks the tier)</SectionLabel>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {tiers.map((tier) => {
            const form = edits[tier.id] ?? formFrom(tier)
            const setForm = (patch: Partial<TierForm>) => setEdits({ ...edits, [tier.id]: { ...form, ...patch } })
            return (
              <div key={tier.id} className="rounded-lg border border-line p-4">
                <div className="flex items-center justify-between">
                  <TierBadge name={form.name || tier.name} color={form.color} icon={form.icon} />
                  <button
                    type="button"
                    title={tiers.length <= 1 ? 'At least one tier must remain' : `Delete ${tier.name}`}
                    disabled={tiers.length <= 1}
                    onClick={() => setConfirmDelete(tier)}
                    className="text-muted transition hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <Input value={form.name} onChange={(e) => setForm({ name: e.target.value })} placeholder="Name" />
                  <Input
                    type="number"
                    min={0}
                    value={form.topup_threshold}
                    onChange={(e) => setForm({ topup_threshold: e.target.value })}
                    placeholder="Threshold (RM)"
                  />
                </div>
                <div className="mt-2">
                  <ColorField value={form.color} onChange={(color) => setForm({ color })} />
                </div>
                <div className="mt-2">
                  <IconPicker value={form.icon} color={form.color} onChange={(icon) => setForm({ icon })} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-2xs text-muted">Top up {money(Number(form.topup_threshold || 0))} total to unlock</p>
                  <Button
                    variant="outline"
                    loading={saveTier.isPending && saveTier.variables?.id === tier.id}
                    onClick={() => saveTier.mutate(tier)}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )
          })}

          {/* Add tier */}
          <div className="rounded-lg border border-dashed border-foreground/25 p-4">
            <TierBadge name={draft.name || 'New tier'} color={draft.color} icon={draft.icon} />
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" />
              <Input
                type="number"
                min={0}
                value={draft.topup_threshold}
                onChange={(e) => setDraft({ ...draft, topup_threshold: e.target.value })}
                placeholder="Threshold (RM)"
              />
            </div>
            <div className="mt-2">
              <ColorField value={draft.color} onChange={(color) => setDraft({ ...draft, color })} />
            </div>
            <div className="mt-2">
              <IconPicker value={draft.icon} color={draft.color} onChange={(icon) => setDraft({ ...draft, icon })} />
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                loading={addTier.isPending}
                disabled={!draft.name.trim() || draft.topup_threshold === ''}
                onClick={() => addTier.mutate()}
              >
                <Plus className="size-4" /> Add tier
              </Button>
            </div>
            <p className="mt-2 text-2xs text-muted">
              New tiers start with prices copied from the closest lower tier - fine-tune them per product in Product
              setup.
            </p>
          </div>
        </div>
      </Card>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={`Delete ${confirmDelete?.name} tier?`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteTier.isPending}
              onClick={() => confirmDelete && deleteTier.mutate(confirmDelete.id)}
            >
              Delete tier
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground/70">
          Its product prices are removed and customers on this tier are re-assigned to the tier matching their lifetime
          top-up. This cannot be undone.
        </p>
      </Modal>
    </div>
  )
}
